import React, { useEffect, useState } from 'react';
import { Menu, Divider, Icon, Container, Grid } from 'semantic';
import { Link, Route, Switch, useLocation } from 'react-router-dom';
import { Breadcrumbs } from 'components/index';
import { useTranslation } from 'react-i18next';
import styles from './MenuItem.module.css';

import SettingsLookAndFeel from './SettingsLookAndFeel';
import SettingsProductProposition from './SettingsProductProposition';
import SettingsPlatformAccess from './SettingsPlatformAccess';
import SettingsSupportMaintenance from './SettingsSupportMaintenance';
import SettingsChargeStations from './SettingsChargeStations';
import SettingsPayments from './SettingsPayments';
import SettingsTariffsAccessManagement from './SettingsTariffsAccessManagement';
import SettingsBilling from './SettingsBilling';
import SettingsRoaming from './SettingsRoaming';
import SettingsOther from './SettingsOther';
import { useUser } from 'contexts/user';
import { useFeatures } from 'contexts/features';
import { request } from 'utils/api';
import { Provider } from 'types/provider';

const menuItems = (t: (k: string, v: string) => string) => [
  {
    title: t('sandbox.settingsMenuLookAndFeel', 'Look and Feel'),
    route: '/sandbox/settings/look-and-feel',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_look_and_feel',
    iconName: 'brush',
  },
  {
    title: t('sandbox.settingsMenuDevelopers', 'Developers'),
    route: '/sandbox/settings/developers',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_developers',
    iconName: 'code',
  },
  {
    title: t('sandbox.settingsMenuProductProposition', 'Product Proposition'),
    route: '/sandbox/settings/product-proposition',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_product_proposition',
    iconName: 'car',
  },
  {
    title: t('sandbox.settingsMenuPlatformAccess', 'Platform Access'),
    route: '/sandbox/settings/platform-access',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_platform_access',
    iconName: 'key',
  },
  {
    title: t('sandbox.settingsMenuSupportMaintenance', 'Support & Maintenance'),
    route: '/sandbox/settings/support-maintenance',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_support_maintenance',
    iconName: 'phone',
  },
  {
    title: t('sandbox.settingsMenuChargeStations', 'Charge Stations'),
    route: '/sandbox/settings/charge-stations',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_charge_stations',
    iconName: 'charging-station',
  },
  {
    title: t('sandbox.settingsMenuPayments', 'Payments'),
    route: '/sandbox/settings/payments',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_payments',
    iconName: 'credit-card',
  },
  {
    title: t(
      'sandbox.settingsMenuTariffsAccessManagement',
      'Tariffs & Access Management'
    ),
    route: '/sandbox/settings/tariffs-access-management',
    sandbox_settings_menu_feature:
      'sandbox_settings_menu_tariffs_access_management',
    iconName: 'chart-line',
  },
  {
    title: t('sandbox.settingsMenuBilling', 'Billing'),
    route: '/sandbox/settings/billing',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_billing',
    iconName: 'file-invoice',
  },
  {
    title: t('sandbox.settingsMenuRoaming', 'Roaming'),
    route: '/sandbox/settings/roaming',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_roaming',
    iconName: 'globe',
  },
  {
    title: t('sandbox.settingsMenuOther', 'Other'),
    route: '/sandbox/settings/other',
    sandbox_settings_menu_feature: 'sandbox_settings_menu_other',
    iconName: 'ellipsis',
  },
];
const MenuItem = ({
  title,
  route,
  iconName,
  active,
}: {
  title: string;
  route: string;
  iconName: string;
  active: boolean;
}) => {
  return (
    <Link to={route} className={`${styles.menuItem}`}>
      <Menu.Item
        active={active}
        className={
          active ? `active item ${styles.activeMenuItem}` : styles.menuItem
        }>
        <Icon name={iconName} />
        <div style={{ fontWeight: 500 }}>{title}</div>
      </Menu.Item>
    </Link>
  );
};

export default function SandboxSettings() {
  const { t } = useTranslation();
  //@ts-ignore
  const [sandboxProvider, setSandboxProvider] = useState<Provider>({});
  const { hasFeature, isReady } = useFeatures();
  const { provider } = useUser();
  const location = useLocation();

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const { data } = await request({
          path: `/1/sandboxes/${provider?.id}`,
          method: 'GET',
        });
        setSandboxProvider(data);
      } catch (e) {
        console.error('provider fetch console error', provider?.id, e);
      }
    };
    fetchProvider();
  }, [provider?.id]);

  return (
    <Container>
      <Breadcrumbs
        path={[
          <Link key="sandbox" to="/sandbox">
            {t('sandbox.title', 'Sandbox')}
          </Link>,
        ]}
      />
      <Container
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '25px',
        }}>
        <Container>
          <Menu vertical style={{ minWidth: '250px' }}>
            <Menu.Header
              as="h4"
              style={{
                marginLeft: 15,
                fontWeight: 'bold',
                marginBottom: 0,
                marginTop: 12,
              }}>
              {t('sandbox.settingsMenuHeader', 'Settings')}
            </Menu.Header>

            <Divider style={{ marginBottom: -1 }} />

            {menuItems(t)
              .filter(
                (x) => hasFeature(x.sandbox_settings_menu_feature) && isReady
              )
              .map(({ title, route, iconName }) => {
                return (
                  <MenuItem
                    key={route}
                    title={title}
                    route={route}
                    active={route === location.pathname}
                    iconName={iconName}
                  />
                );
              })}
          </Menu>
        </Container>
        <Container>
          <Switch>
            <Route
              path="/sandbox/settings"
              component={(props: any) => (
                <SettingsLookAndFeel provider={sandboxProvider} {...props} />
              )}
              exact
            />
            <Route
              path="/sandbox/settings/look-and-feel"
              component={(props: any) => (
                <SettingsLookAndFeel provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/product-proposition"
              component={(props: any) => (
                <SettingsProductProposition
                  provider={sandboxProvider}
                  {...props}
                />
              )}
            />
            <Route
              path="/sandbox/settings/platform-access"
              component={(props: any) => (
                <SettingsPlatformAccess provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/support-maintenance"
              component={(props: any) => (
                <SettingsSupportMaintenance
                  provider={sandboxProvider}
                  {...props}
                />
              )}
            />
            <Route
              path="/sandbox/settings/charge-stations"
              component={(props: any) => (
                <SettingsChargeStations provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/payments"
              component={(props: any) => (
                <SettingsPayments provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/tariffs-access-management"
              component={(props: any) => (
                <SettingsTariffsAccessManagement
                  provider={sandboxProvider}
                  {...props}
                />
              )}
            />
            <Route
              path="/sandbox/settings/billing"
              component={(props: any) => (
                <SettingsBilling provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/roaming"
              component={(props: any) => (
                <SettingsRoaming provider={sandboxProvider} {...props} />
              )}
            />
            <Route
              path="/sandbox/settings/other"
              component={(props: any) => (
                <SettingsOther provider={sandboxProvider} {...props} />
              )}
            />
          </Switch>
        </Container>
      </Container>
    </Container>
  );
}
